import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { database } from "../constants/firebase";
import { ref, onValue, off, remove } from "firebase/database";
import { useUser } from "../contexts/UserContext";
import MobileHeader from "../components/MobileHeader";
import { LuUsers, LuPlus, LuPencil, LuTrash2 } from "react-icons/lu";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import { ROUTES } from "../App";
import ConfirmationModal from "../components/ConfirmationModal";

const AdminChallenges = () => {
    const { currentUser } = useUser();
    const navigate = useMoveTo();

    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showConfirmDelete, setShowConfirmDelete] = useState(null);

    useEffect(() => {
        if (!currentUser?.isAdmin) return;

        const challengesRef = ref(database, "Challenges");
        const unsub = onValue(challengesRef, (snapshot) => {
            if (!snapshot.exists()) {
                setChallenges([]);
                setLoading(false);
                return;
            }

            const data = snapshot.val();
            const list = Object.entries(data).map(([key, value]) => ({
                id: key,
                ...value,
                applicantCount: value.applications ? Object.keys(value.applications).length : 0
            }));

            // Sort by month (newest first)
            setChallenges(list.sort((a, b) => b.id.localeCompare(a.id)));
            setLoading(false);
        });

        return () => off(challengesRef);
    }, [currentUser]);

    const handleCreateNew = () => {
        navigate(ROUTES.ADMIN_MANAGE_CHALLENGE.replace("/:monthKey?", ""));
    };

    const handleEdit = (monthKey) => {
        navigate(ROUTES.ADMIN_MANAGE_CHALLENGE.replace(":monthKey?", monthKey));
    };

    const handleViewApplicants = (monthKey) => {
        navigate(ROUTES.ADMIN_CHALLENGE_APPLICANTS.replace(":monthKey", monthKey));
    };

    const confirmDelete = async () => {
        if (!showConfirmDelete) return;
        try {
            await remove(ref(database, `Challenges/${showConfirmDelete}`));
            window.showToast("Challenge deleted", "success");
        } catch (err) {
            window.showToast("Failed to delete", "error");
        } finally {
            setShowConfirmDelete(null);
        }
    };

    if (!currentUser?.isAdmin) {
        return <div className="h-screen flex items-center justify-center dark:bg-[#1A1A1A] dark:text-white">Access Denied</div>;
    }

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title={"Manage Challenges"} isBackBtn={true} />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-gray-100 dark:border-gray-800 gap-4">
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate(-1)} className="hidden md:block">
                                <img src={ICONS.arrowBack} className="w-5 h-5 grayscale brightness-0 dark:invert" alt="Back" />
                            </button>
                            <h2 className="text-2xl md:text-3xl font-bold">Monthly Challenges</h2>
                        </div>
                        <button 
                            onClick={handleCreateNew}
                            className="bg-[#327691] hover:bg-[#2a637a] text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center space-x-2 shadow-lg shadow-[#327691]/20"
                        >
                            <LuPlus className="text-lg" />
                            <span>New Challenge</span>
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="bg-white dark:bg-[#1f1f1f] rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-white/5">
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Month</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Challenge Title</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Applicants</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest">Status</th>
                                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                        {loading ? (
                                            <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-400">Loading challenges...</td></tr>
                                        ) : challenges.length > 0 ? challenges.map((c) => (
                                            <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4 font-bold text-sm text-[#327691] uppercase">{c.id}</td>
                                                <td className="px-6 py-4">
                                                    <p className="font-semibold text-sm line-clamp-1">{c.title}</p>
                                                    <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{c.description}</p>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button 
                                                        onClick={() => handleViewApplicants(c.id)}
                                                        className="flex items-center justify-center mx-auto space-x-1.5 hover:text-[#327691] transition-colors group/count"
                                                        disabled={c.applicantCount === 0}
                                                    >
                                                        <LuUsers className="text-gray-400 w-3 h-3 group-hover/count:text-[#327691]" />
                                                        <span className={`text-sm font-bold ${c.applicantCount > 0 ? 'underline decoration-dotted' : ''}`}>{c.applicantCount}</span>
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                        {c.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button 
                                                            onClick={() => handleEdit(c.id)}
                                                            className="p-2 hover:bg-[#327691]/10 text-[#327691] rounded-lg transition-colors"
                                                            title="Edit Parameters"
                                                        >
                                                            <LuPencil className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => setShowConfirmDelete(c.id)}
                                                            className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors"
                                                            title="Delete"
                                                        >
                                                            <LuTrash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="5" className="px-6 py-20 text-center text-gray-400 italic">No challenges found. Create the first one!</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            <ConfirmationModal 
                isOpen={!!showConfirmDelete}
                onClose={() => setShowConfirmDelete(null)}
                onConfirm={confirmDelete}
                title="Delete Challenge"
                confirmText="Delete"
                Icon={LuTrash2}
                message="Are you sure you want to delete this challenge? This will remove all associated applicant data as well."
            />
        </div>
    );
};

export default AdminChallenges;
